import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import PropTypes from 'prop-types';
import FiltersSection from '../../../components/molecules/FiltersSection/FiltersSection';
import { GET_CREATIVE_TAGS } from '../../queries';
import { CreativeType } from '../../types';
import { isObjectEmpty, trackMixpanelEvent } from '../../../utils/general';
import { GalleryContext, DEFAULT_AI_FILTERS, TRACK_EVENT_PREFIX } from '../../consts';
import { ASYNC_FILTER_TO_FIELD_CONFIG, DimensionCategory, GeneralDimensionsConfig } from '../../dimensionsUtils';
import {
    filterCreativesByAIDimensions,
    filterCreativesByGeneralDimensions,
    getAutocompleteOptions,
    getMultiValuedDimensionTags,
    getSingleValuedDimensionTags,
    isAsyncFilter,
    isEqualCreatives,
    isGeneralDimension,
} from './filtersUtils';
import { useUrlParams } from '../../../utils/useUrlParamsHook';
import DimensionIcon from './dimensionIcon/DimensionIcon';

export const ADD_FILTER_TEXT = 'STATIC.PAGES.CREATIVE_GALLERY.ADD_FILTER_BUTTON_TEXT';
export const FILTERS_TITLE = 'STATIC.PAGES.CREATIVE_GALLERY.FILTERS_SECTION_TITLE';

const DEFAULT_GENERAL_DIMENSIONS = Object.keys(GeneralDimensionsConfig).filter(
    filter => GeneralDimensionsConfig[filter].isDefault
);

const doAllFiltersHasOptions = (filters, filterToCreativeTags) =>
    !filters.some(filter => !filterToCreativeTags[filter]?.tags?.length);

const CreativeGalleryFilters = ({
    app,
    source,
    creatives = [],
    disabled = false,
    onFilteredCreativesChange = () => {},
    isLoadingMultiGeneralDimensions = false,
    onAsyncLoad,
}) => {
    const { dimensionsByCategory } = useContext(GalleryContext);
    const { getParamsObject, setParamsObject } = useUrlParams();

    const lastFetchedCreativesRef = useRef();
    const isUrlParamsLoaded = useRef(false);
    const shouldRunAsyncFilter = useRef(false);
    const shouldUpdateFiltersOptions = useRef(true);
    const [queryCreativeTags, { data: creativeFilterTagsResponse }] = useLazyQuery(GET_CREATIVE_TAGS);

    const [selectedFilters, setSelectedFilters] = useState(DEFAULT_GENERAL_DIMENSIONS);
    const [loadingFilters, setLoadingFilters] = useState([]);
    const [filterToSelectedTags, setFilterToSelectedTags] = useState({});
    const [filterToCreativeTags, setFilterToCreativeTags] = useState({});
    const [selectedAsyncFilters, setSelectedAsyncFilters] = useState([]);

    const allSelectedAsyncFiltersHasOptions = doAllFiltersHasOptions(selectedAsyncFilters, filterToCreativeTags);

    const queryCreativeTagsForFilters = filterNames => {
        setLoadingFilters(prev => prev.concat(filterNames));
        queryCreativeTags({
            variables: {
                appId: app,
                imageHashes: creatives.map(({ imageHash }) => imageHash),
                dimensionNames: filterNames,
            },
        });
    };

    const trackEvent = useCallback(
        (eventName, params) =>
            trackMixpanelEvent(TRACK_EVENT_PREFIX, eventName, {
                app,
                source,
                ...params,
            }),
        [app, source]
    );

    const filtersTagsOptions = useMemo(
        () =>
            Object.entries(filterToCreativeTags).reduce((filterToOptions, [filter, { tags }]) => {
                filterToOptions[filter] = tags;
                return filterToOptions;
            }, {}),
        [filterToCreativeTags]
    );

    const { generalDimensions, aiDimensions } = useMemo(
        () =>
            selectedFilters.reduce(
                (acc, filterName) => {
                    if (isGeneralDimension(filterName)) acc.generalDimensions.push(filterName);
                    else acc.aiDimensions.push(filterName);
                    return acc;
                },
                { generalDimensions: [], aiDimensions: [] }
            ),
        [selectedFilters]
    );

    // Combine general dimensions and AI dimensions into a single structure
    const filtersByCategory = useMemo(() => {
        const filters = {
            [DimensionCategory.GENERAL_DIMENSIONS]: Object.keys(GeneralDimensionsConfig),
        };

        if (dimensionsByCategory) {
            Object.entries(dimensionsByCategory).forEach(([category, dims]) => {
                if (dims?.length) {
                    filters[category] = dims.map(({ name }) => name);
                }
            });
        }

        return filters;
    }, [dimensionsByCategory]);

    useEffect(() => {
        if (!creativeFilterTagsResponse?.creativeTags) return;

        lastFetchedCreativesRef.current = creatives;

        const { dimensionNames, tags } = creativeFilterTagsResponse?.creativeTags;

        setLoadingFilters(prev =>
            !dimensionNames?.length ? [] : prev.filter(filter => !dimensionNames.includes(filter))
        );

        if (!tags?.length) return;

        const tagsByFilter = tags.reduce((acc, tag) => {
            acc[tag.dimensionName] = acc[tag.dimensionName] || [];
            acc[tag.dimensionName].push({
                imageHash: tag.imageHash,
                category: tag.category,
                tags: tag.tags,
            });
            return acc;
        }, {});

        setFilterToCreativeTags(prev => ({
            ...prev,
            ...dimensionNames.reduce((acc, filterName) => {
                const filterTags = tagsByFilter[filterName] || [];
                acc[filterName] = {
                    tags: getAutocompleteOptions(filterTags),
                    options: filterTags,
                };
                return acc;
            }, {}),
        }));
    }, [creativeFilterTagsResponse?.creativeTags]);

    useEffect(() => {
        // If no AI dimensions or any previous query have been made
        if (disabled || !dimensionsByCategory || lastFetchedCreativesRef.current) return;

        const allDimensions = new Set(
            Object.values(dimensionsByCategory).flatMap(dimensions => dimensions.map(({ name }) => name))
        );
        const appDefaultFilters = DEFAULT_AI_FILTERS.filter(dimension => allDimensions.has(dimension));
        if (!appDefaultFilters.length) return;

        setSelectedFilters(prev => [...prev, ...appDefaultFilters]);
        queryCreativeTagsForFilters(appDefaultFilters);
    }, [dimensionsByCategory, disabled]);

    useEffect(() => {
        if (!creatives.length) return;
        let filteredCreatives = filterCreativesByGeneralDimensions(creatives, generalDimensions, filterToSelectedTags);

        filteredCreatives = aiDimensions.length
            ? filterCreativesByAIDimensions(filteredCreatives, aiDimensions, filterToSelectedTags, filterToCreativeTags)
            : filteredCreatives;

        onFilteredCreativesChange(filteredCreatives);
    }, [creatives, selectedFilters, filterToCreativeTags, filterToSelectedTags]);

    useEffect(() => {
        if (disabled || !isUrlParamsLoaded.current) return;

        setParamsObject({
            selectedFilters,
            filterToSelectedTags,
        });
    }, [selectedFilters, filterToSelectedTags, disabled]);

    const resetFiltersConfigs = () => {
        setLoadingFilters(prev => (prev.length ? [] : prev));
        setFilterToSelectedTags(prev => (isObjectEmpty(prev) ? prev : {}));
        setSelectedAsyncFilters([]);
    };

    const handleAsyncFilterSelection = () => {
        const filters = selectedAsyncFilters
            .map(filterName => {
                const asyncConfig = ASYNC_FILTER_TO_FIELD_CONFIG[filterName];
                if (!asyncConfig) return null;
                const selectedTags = filterToSelectedTags[filterName] || [];
                // If we selected all tags, then that the same filter as selecting none, therefore we will send an empty array
                return {
                    filterName,
                    dimension: asyncConfig.originalField,
                    values: selectedTags.length === (filtersTagsOptions[filterName]?.length || 0) ? [] : selectedTags,
                };
            })
            .filter(Boolean);

        onAsyncLoad(filters);
    };

    const setAsyncFilters = asyncFilters => {
        setSelectedAsyncFilters(asyncFilters);
        shouldRunAsyncFilter.current = true;
    };

    useEffect(() => {
        if (disabled) {
            resetFiltersConfigs();
            return;
        }

        const urlParams = getParamsObject();

        if (isObjectEmpty(urlParams)) return;

        const { selectedFilters: filters, filterToSelectedTags: filterTags } = urlParams;
        isUrlParamsLoaded.current = true;

        if (!filters) return;

        if (filters.length) {
            setSelectedFilters(filters);
            const filtersToQuery = filters.filter(name => !isGeneralDimension(name) && !filterToCreativeTags[name]);
            if (filtersToQuery.length) queryCreativeTagsForFilters(filtersToQuery);
            if (!isObjectEmpty(filterTags)) {
                setFilterToSelectedTags(filterTags);
                const asyncFiltersWithTags = Object.keys(filterTags).filter(
                    filter => isAsyncFilter(filter) && filterTags[filter]?.length
                );
                asyncFiltersWithTags.length && setAsyncFilters(asyncFiltersWithTags);
            }
        } else {
            setSelectedFilters([]);
        }
    }, [disabled]);

    useEffect(() => {
        if (!shouldRunAsyncFilter.current || !allSelectedAsyncFiltersHasOptions) return;

        handleAsyncFilterSelection();
        shouldRunAsyncFilter.current = false;
    }, [filterToSelectedTags, allSelectedAsyncFiltersHasOptions]);

    useEffect(() => {
        if (!creatives.length || !shouldUpdateFiltersOptions.current) return;

        const addedAsyncFilters = selectedFilters.filter(isAsyncFilter);

        if (addedAsyncFilters.length && !selectedAsyncFilters.length) {
            setLoadingFilters(prev =>
                isLoadingMultiGeneralDimensions
                    ? [...prev, ...addedAsyncFilters]
                    : prev.filter(d => !addedAsyncFilters.includes(d))
            );
        }

        if (selectedAsyncFilters.length && allSelectedAsyncFiltersHasOptions) {
            shouldUpdateFiltersOptions.current = false;
            setLoadingFilters(prev => prev.filter(d => !addedAsyncFilters.includes(d)));
        }

        setFilterToCreativeTags(prev => ({
            ...prev,
            ...Object.entries(GeneralDimensionsConfig).reduce((acc, [filterName, config]) => {
                const tags = (config.isMultiValued ? getMultiValuedDimensionTags : getSingleValuedDimensionTags)(
                    creatives,
                    config.creativeField
                );

                acc[filterName] = { tags };
                return acc;
            }, {}),
        }));
    }, [isLoadingMultiGeneralDimensions, creatives]);

    useEffect(() => {
        if (!creatives.length) {
            resetFiltersConfigs();
            return;
        }

        const lastFetchedCreatives = lastFetchedCreativesRef.current || [];

        if (aiDimensions.length && lastFetchedCreatives.length && !isEqualCreatives(creatives, lastFetchedCreatives)) {
            const oldCreativeHashes = new Set(lastFetchedCreatives.map(({ imageHash }) => imageHash));
            const areAllCreativesQueriedBefore = creatives.every(creative => oldCreativeHashes.has(creative.imageHash));
            if (!areAllCreativesQueriedBefore) {
                queryCreativeTagsForFilters(aiDimensions);
                lastFetchedCreativesRef.current = creatives;
                shouldUpdateFiltersOptions.current = true;
            }
        }
    }, [creatives]);

    const handleFilterAdded = filterName => {
        const updatedFilters = [...selectedFilters, filterName];
        setSelectedFilters(updatedFilters);

        trackEvent('AI Dimension Selected', {
            selectedDimension: filterName,
            updatedDimensions: updatedFilters,
        });

        if (!filterToCreativeTags[filterName] && !isGeneralDimension(filterName)) {
            queryCreativeTagsForFilters([filterName]);
        }
    };

    const handleFilterRemoved = filter => {
        const updatedFilters = selectedFilters.filter(d => d !== filter);
        setSelectedFilters(updatedFilters);
        isAsyncFilter(filter) && setAsyncFilters(selectedAsyncFilters.filter(d => d !== filter));

        setFilterToSelectedTags(prev => {
            const updatedTags = { ...prev };
            delete updatedTags[filter];
            return updatedTags;
        });

        trackEvent('AI Dimension Removed', {
            removedDimensions: filter,
            updatedDimensions: updatedFilters,
        });
    };

    const handleClearAll = () => {
        setSelectedFilters([]);
        setFilterToSelectedTags({});
        trackEvent('AI Dimension Removed', { removedDimensions: selectedFilters, updatedDimensions: [] });
        trackEvent('AI Filter Changed', { updatedAIFilters: {} });
    };

    const handleFilterTagsChange = (filterName, selectedTags) => {
        const tagsValues = selectedTags.map(({ value }) => value);
        const updatedTags = {
            ...filterToSelectedTags,
            [filterName]: tagsValues,
        };
        setFilterToSelectedTags(updatedTags);
        trackEvent('AI Filter Changed', { updatedAIFilters: updatedTags });

        if (!isAsyncFilter(filterName)) return;

        if (selectedAsyncFilters.includes(filterName)) {
            shouldRunAsyncFilter.current = true;
        } else {
            setAsyncFilters([...selectedAsyncFilters, filterName]);
        }
    };

    // Create a filter object for FiltersSection
    const filters = useMemo(() => {
        return selectedFilters.reduce((filtersObj, filterName) => {
            const options = filtersTagsOptions[filterName] || [];
            const selectedValues = filterToSelectedTags[filterName] || [];

            // Create selected options objects from selected values
            const selectedOptions = options.filter(option => selectedValues.includes(option.value));

            filtersObj[filterName] = {
                name: filterName,
                displayName: filterName,
                options,
                selectedOptions,
                loading: loadingFilters.includes(filterName),
                removable: true,
            };

            return filtersObj;
        }, {});
    }, [selectedFilters, filtersTagsOptions, filterToSelectedTags, loadingFilters]);

    return (
        <FiltersSection
            filters={filters}
            filtersByCategory={filtersByCategory}
            onFilterAdded={handleFilterAdded}
            onFilterRemoved={handleFilterRemoved}
            onFilterTagsChange={handleFilterTagsChange}
            onClearAll={handleClearAll}
            disabled={disabled}
            addFilterText={ADD_FILTER_TEXT}
            headerTitle={FILTERS_TITLE}
            headerIcon="filter-plus"
            PopoverIconComponent={DimensionIcon}
            addFilterIconName="ai-line"
        />
    );
};

CreativeGalleryFilters.propTypes = {
    app: PropTypes.string,
    source: PropTypes.string,
    creatives: PropTypes.arrayOf(PropTypes.shape(CreativeType)),
    disabled: PropTypes.bool,
    onFilteredCreativesChange: PropTypes.func,
    isLoadingMultiGeneralDimensions: PropTypes.bool,
    onAsyncLoad: PropTypes.func,
};

export default CreativeGalleryFilters;
